/**
 * /pricing — Public-facing pricing page
 *
 * Monthly-only (no yearly toggle for launch).
 * Plans:
 *   Host           — Free forever
 *   Individual     — $9.99 CAD/mo flat, up to 3 properties
 *   Strata Council — $18.99 CAD/mo flat  (price_1TBlVNGY50i6mP3m75Zzma6u)
 *   Property Mgr   — $28.99 CAD/building/mo tiered (price_1TBlXmGY50i6mP3maQ1YdDZH)
 */
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Check, Building2, Users, Home,
  Loader2, MessageSquare, Shield, BookOpen, FileCheck,
  BarChart3, Wrench, Bell, CreditCard, Minus, Plus,
  Zap, Lock, ClipboardList, UserCog, CalendarDays, TrendingUp, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { BrandLogo } from "@/components/BrandLogo";
import { PublicFooter } from "@/components/PublicFooter";
import { CurrencyToggle } from "@/components/CurrencyToggle";
import { useAuth } from "@/contexts/AuthContext";
import { useCreateCheckoutSession, usePublicCheckout, useClaimCheckoutSession, useSubscription } from "@/hooks/useSubscription";
import { useToast } from "@/hooks/use-toast";
import { useCurrency } from "@/hooks/useCurrency";
import {
  trackPricingView, trackPlanSelected, trackBuildingCountChanged,
  initScrollDepthTracking, initOutboundLinkTracking,
} from "@/lib/ga4";

// ─── Price IDs (from Stripe Dashboard) ───────────────────────────────────────

const PRICE_INDIVIDUAL = import.meta.env.VITE_STRIPE_PRICE_INDIVIDUAL_MONTHLY ?? "price_1TBpsyGY50i6mP3mGqkogH1z";
const PRICE_COUNCIL    = import.meta.env.VITE_STRIPE_PRICE_COUNCIL_MONTHLY    ?? "price_1TBlVNGY50i6mP3m75Zzma6u";
const PRICE_MANAGER    = import.meta.env.VITE_STRIPE_PRICE_MANAGER_MONTHLY    ?? "price_1TBlXmGY50i6mP3maQ1YdDZH";

// ─── Feature lists ─────────────────────────────────────────────────────────

const individualFeatures = [
  { icon: Home,          label: "Up to 5 properties — houses, condos, suites, long-term rentals" },
  { icon: BookOpen,      label: "Booking management with auto-approval & night tracking" },
  { icon: CalendarDays,  label: "Rental calendar — visual month-view of all bookings" },
  { icon: TrendingUp,    label: "Income & expense tracker per property with running balance" },
  { icon: Home,          label: "Guest portal — secure check-in link, no account needed" },
  { icon: FileCheck,     label: "Documents: permits, leases, receipts, insurance & more" },
  { icon: Wrench,        label: "Maintenance log — track open issues per property" },
  { icon: CreditCard,    label: "Dispute notes — log tenant or neighbour issues" },
  { icon: Download,      label: "Property summary CSV export — great for tax time" },
  { icon: MessageSquare, label: "In-app & email notifications · 7-day free trial" },
];

const hostFeatures = [
  { icon: BookOpen,      label: "Manage your STR independently — no council or PM needed to start" },
  { icon: Home,          label: "Full booking management & night tracking" },
  { icon: FileCheck,     label: "Upload & manage STR documents (permits, licences, insurance)" },
  { icon: Home,          label: "Guest portal — secure check-in link, no account needed" },
  { icon: Wrench,        label: "Log & track maintenance requests" },
  { icon: MessageSquare, label: "Messaging — connect with manager or council when linked" },
  { icon: Bell,          label: "In-app & email notifications" },
  { icon: Shield,        label: "Join a strata building later by accepting an invitation" },
];

const councilFeatures = [
  { icon: Shield,        label: "STR compliance automation — by-law rules enforced automatically" },
  { icon: BarChart3,     label: "Booking approvals with automated night-limit enforcement" },
  { icon: Home,          label: "Guest portal — guests get a secure link, no account needed" },
  { icon: FileCheck,     label: "Host document management & expiry tracking" },
  { icon: CreditCard,    label: "Dispute tracking & resolution log" },
  { icon: Wrench,        label: "Amenity scheduling & maintenance oversight" },
  { icon: Building2,     label: "Flat rate — one building, unlimited hosts" },
];

const managerFeatures = [
  { icon: Building2,     label: "Multi-building dashboard — all properties in one view" },
  { icon: Lock,          label: "Council-delegated permissions — each council controls exactly what you can modify" },
  { icon: Bell,          label: "Daily digest emails — automated morning summaries across all buildings" },
  { icon: BarChart3,     label: "Cross-building reports & analytics — AGM-ready exports per building" },
  { icon: ClipboardList, label: "Granular audit trail — every action logged, timestamped & exportable" },
  { icon: UserCog,       label: "Team management — add staff with scoped per-building access" },
  { icon: Zap,           label: "Registry STR validation — registration number checks (coming soon)" },
  { icon: MessageSquare, label: "Priority support" },
];

// ─── Plan Card ────────────────────────────────────────────────────────────────

interface PlanCardProps {
  icon: React.ElementType;
  title: string;
  badge?: string;
  badgeVariant?: "default" | "secondary" | "outline";
  price: React.ReactNode;
  priceNote?: string;
  description: string;
  featuresLabel?: string;
  features: { icon: React.ElementType; label: string }[];
  cta: string;
  ctaVariant?: "default" | "outline";
  onCta: () => void;
  ctaLoading?: boolean;
  highlighted?: boolean;
  extra?: React.ReactNode;
}

function PlanCard({
  icon: PlanIcon, title, badge, badgeVariant = "secondary",
  price, priceNote, description, featuresLabel, features, cta,
  ctaVariant = "default", onCta, ctaLoading = false,
  highlighted = false, extra,
}: PlanCardProps) {
  return (
    <Card className={`relative flex flex-col ${
      highlighted
        ? "border-primary shadow-xl shadow-primary/15 ring-2 ring-primary/40 scale-[1.02]"
        : ""
    }`}>
      <CardHeader className="pb-0 pt-6 px-6">
        <div className="flex items-start gap-3 mb-4">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
            highlighted ? "bg-primary/20" : "bg-primary/10"
          }`}>
            <PlanIcon className={`h-5 w-5 ${highlighted ? "text-primary" : "text-primary"}`} />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-bold leading-tight">{title}</h3>
            {badge && (
              <Badge variant={badgeVariant} className="mt-1 text-[10px] h-4 px-1.5">{badge}</Badge>
            )}
          </div>
        </div>
        <div className="mb-1">{price}</div>
        {priceNote && <p className="text-xs text-muted-foreground mt-1">{priceNote}</p>}
        <p className="text-xs text-muted-foreground mt-2 mb-4">{description}</p>
        {extra}
      </CardHeader>
      <CardContent className="flex-1 flex flex-col px-6 pb-6">
        <Button
          className={`w-full mb-5 ${highlighted ? "shadow-md" : ""}`}
          variant={ctaVariant}
          onClick={onCta}
          disabled={ctaLoading}
        >
          {ctaLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Redirecting…</> : cta}
        </Button>
        {featuresLabel && (
          <p className="text-xs font-semibold text-muted-foreground mb-2.5 italic">{featuresLabel}</p>
        )}
        <ul className="space-y-2.5">
          {features.map(({ icon: FIcon, label }) => (
            <li key={label} className="flex items-start gap-2 text-xs">
              <div className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15">
                <Check className="h-2 w-2 text-emerald-600" />
              </div>
              <span className="text-muted-foreground leading-snug">{label}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Pricing() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const createCheckout = useCreateCheckoutSession();
  const publicCheckout = usePublicCheckout();
  const claimCheckout = useClaimCheckoutSession();
  const { isActive, requiresSubscription } = useSubscription();
  const [buildings, setBuildings] = useState(1);
  const { currency, setCurrency, isCAD } = useCurrency();

  // Checkout-first flow: after email verification, user lands here with ?checkout_session=X
  const pendingSession = searchParams.get("checkout_session");
  const hasClaimed = useRef(false);

  useEffect(() => {
    if (!user || !pendingSession || hasClaimed.current || claimCheckout.isPending) return;
    hasClaimed.current = true;
    claimCheckout.mutate(
      { checkoutSessionId: pendingSession },
      {
        onSuccess: () => {
          toast({ title: "Plan activated!", description: "Your subscription is now active. Welcome!" });
          navigate("/dashboard", { replace: true });
        },
        onError: (err) => {
          // Session may already be claimed (idempotent) — redirect anyway
          if (err.message?.includes("already been claimed")) {
            navigate("/dashboard", { replace: true });
          } else {
            toast({ title: "Could not activate plan", description: err.message, variant: "destructive" });
          }
        },
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pendingSession]);

  const handleSubscribe = (priceId: string, role: string, qty = 1) => {
    if (!user) {
      // Pay-first flow: go straight to Stripe, sign up after
      publicCheckout.mutate({ priceId, plan: role, quantity: qty });
      return;
    }
    createCheckout.mutate({ priceId, userId: user.id, role, quantity: qty });
  };

  const checkoutLoading = createCheckout.isPending || publicCheckout.isPending;

  const canAccessDashboard = !!user && (!requiresSubscription || isActive);

  const perBuilding    = isCAD ? 28.99 : 21.99;
  const managerMonthly = parseFloat((buildings * perBuilding).toFixed(2));

  // GA4: fire pricing_view once on mount, attach scroll depth + outbound tracking
  useEffect(() => {
    trackPricingView();
    const cleanScroll   = initScrollDepthTracking();
    const cleanOutbound = initOutboundLinkTracking();
    return () => { cleanScroll(); cleanOutbound(); };
  }, []);

  // GA4: track building count changes (skip initial mount)
  const buildingsInitialized = useRef(false);
  useEffect(() => {
    if (!buildingsInitialized.current) { buildingsInitialized.current = true; return; }
    trackBuildingCountChanged(buildings);
  }, [buildings]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo size="sm" />
          </Link>
          <div className="flex items-center gap-3">
            {authLoading ? (
              <div className="h-8 w-48 rounded-md bg-muted/50 animate-pulse" />
            ) : user ? (
              canAccessDashboard ? (
                <Button size="sm" variant="outline" onClick={() => navigate("/dashboard")}>
                  Go to Dashboard
                </Button>
              ) : (
                <span className="text-xs text-muted-foreground">
                  Subscribe below to access the dashboard
                </span>
              )
            ) : (
              <>
                <Button size="sm" variant="ghost" onClick={() => navigate("/login")}>
                  Sign in
                </Button>
                <Button size="sm" onClick={() => navigate("/signup")}>
                  Get started free
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-14 px-4 text-center">
        <Badge variant="secondary" className="mb-4">Pricing</Badge>
        <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl mb-3">
          Simple, transparent pricing
        </h1>
        <p className="text-muted-foreground max-w-xl mx-auto text-base">
          Strata management shouldn't cost a fortune. Hosts are always free.
          Councils & managers pay only for what they need — no long-term contracts, cancel any time.
        </p>
        <div className="flex flex-col items-center gap-2 mt-4">
          <CurrencyToggle currency={currency} onChange={setCurrency} />
          <p className="text-xs text-muted-foreground">
            {isCAD
              ? "All prices in CAD · Billed monthly · HST/GST may apply"
              : "All prices in USD · Taxes may apply"
            }
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="flex-1 px-4 pb-20">
        <div className="mx-auto max-w-6xl grid grid-cols-1 gap-8 md:grid-cols-4 items-start">

          {/* ── 0. Host — Free ── */}
          <PlanCard
            icon={Home}
            title="Host"
            badge="Free forever"
            badgeVariant="secondary"
            price={
              <div>
                <span className="text-3xl font-bold">$0</span>
                <span className="text-muted-foreground text-sm ml-1">/ mo</span>
              </div>
            }
            description="Manage your STR independently from day one — no waiting for an invitation. Track bookings, run your guest portal, and upload documents solo. If your building has a strata council or PM, they can invite you to link in for compliance later."
            features={hostFeatures}
            cta={
              !user
                ? "Get started free"
                : user.role === "host"
                  ? "Go to Host Dashboard"
                  : "Get started free"
            }
            ctaVariant="outline"
            onCta={() => {
              trackPlanSelected("host");
              if (!user) { navigate("/signup?plan=host"); return; }
              if (user.role === "host") { navigate("/host-dashboard"); return; }
              navigate("/signup?plan=host");
            }}
          />

          {/* ── 1. Individual — $9.99 CAD / $7.99 USD ── */}
          <PlanCard
            icon={Home}
            title="Individual"
            badge="New"
            badgeVariant="default"
            price={
              <div>
                <span className="text-3xl font-bold">{isCAD ? "CA$9.99" : "US$7.99"}</span>
                <span className="text-muted-foreground text-sm ml-1">/ mo</span>
              </div>
            }
            priceNote={`Up to 5 properties · 7-day free trial · ${currency}`}
            description="For independent landlords managing their own properties — income tracking, rental calendar, and CSV exports included. No strata council needed."
            features={individualFeatures}
            cta={user?.role === "individual" && isActive ? "Go to My Dashboard" : `Start free trial — ${isCAD ? "CA$9.99" : "US$7.99"}/mo`}
            ctaVariant="outline"
            ctaLoading={checkoutLoading}
            onCta={() => {
              if (user?.role === "individual" && isActive) { navigate("/individual-dashboard"); return; }
              trackPlanSelected("individual");
              handleSubscribe(PRICE_INDIVIDUAL, "individual", 1);
            }}
          />

          {/* ── 2. Strata Council — $18.99 CAD / $13.99 USD ── */}
          <PlanCard
            icon={Building2}
            title="Strata Council"
            badge="Best Value"
            badgeVariant="outline"
            price={
              <div>
                <span className="text-3xl font-bold">{isCAD ? "CA$18.99" : "US$13.99"}</span>
                <span className="text-muted-foreground text-sm ml-1">/ mo</span>
              </div>
            }
            priceNote={`Flat monthly rate — covers your entire building · 7-day free trial · ${currency}`}
            description="For strata councils that self-manage their STR compliance, booking approvals, and host communications."
            features={councilFeatures}
            cta={user?.role === "strata_admin" ? `Subscribe — ${isCAD ? "CA$18.99" : "US$13.99"}/mo` : `Get started — ${isCAD ? "CA$18.99" : "US$13.99"}/mo`}
            ctaLoading={checkoutLoading}
            onCta={() => {
              trackPlanSelected("strata_council");
              handleSubscribe(PRICE_COUNCIL, "strata_admin", 1);
            }}
          />

          {/* ── 3. Property Manager — $28.99 CAD / $21.99 USD per building ── */}
          <PlanCard
            icon={Users}
            title="Property Manager"
            highlighted
            price={
              <div>
                <span className="text-3xl font-bold">{isCAD ? "CA$" : "US$"}{managerMonthly}</span>
                <span className="text-muted-foreground text-sm ml-1">/ mo</span>
              </div>
            }
            priceNote={`$${perBuilding} ${currency} × ${buildings} building${buildings !== 1 ? "s" : ""} = $${managerMonthly}/mo · 7-day free trial`}
            description="For professional property management companies running STR compliance across multiple strata buildings."
            featuresLabel="Everything in Strata Council, plus:"
            features={managerFeatures}
            cta={`Subscribe — $${managerMonthly}/mo ${currency}`}
            ctaLoading={checkoutLoading}
            onCta={() => {
              trackPlanSelected("property_manager");
              handleSubscribe(PRICE_MANAGER, "property_manager", buildings);
            }}
            extra={
              <div className="mb-4">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  How many buildings?
                </p>
                <div className="inline-flex items-center gap-3 rounded-lg border bg-muted/40 px-3 py-1.5">
                  <button
                    type="button"
                    className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-background text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                    onClick={() => setBuildings((b) => Math.max(1, b - 1))}
                    disabled={buildings <= 1}
                    aria-label="Decrease buildings"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="min-w-[1.5rem] text-center text-sm font-bold tabular-nums">
                    {buildings}
                  </span>
                  <button
                    type="button"
                    className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-background text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                    onClick={() => setBuildings((b) => Math.min(50, b + 1))}
                    disabled={buildings >= 50}
                    aria-label="Increase buildings"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            }
          />
        </div>
      </section>

      {/* Feature comparison note */}
      <section className="border-t px-4 py-10 bg-muted/30">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-lg font-bold mb-2">Everything you need to run a compliant building</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Every paid plan includes direct messaging to hosts, owners & guests — both in-app
            notifications and email delivery. No extra charge for communication features.
          </p>
          <div className="grid grid-cols-2 gap-4 text-left sm:grid-cols-4 text-xs text-muted-foreground">
            {[
              "Unlimited bookings",
              "Document management",
              "Real-time notifications",
              "Direct messaging",
              "Compliance reporting",
              "Dispute tracking",
              "Amenity management",
              "Cancel any time",
            ].map((feat) => (
              <div key={feat} className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                <span>{feat}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 py-12">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-lg font-bold text-center mb-6">Frequently asked questions</h2>
          <dl className="space-y-5">
            {[
              {
                q: "Is there a free trial?",
                a: "Yes — all paid plans (Strata Council and Property Manager) include a 7-day free trial. No charge until the trial ends. Cancel before the trial ends and you won't be billed.",
              },
              {
                q: "Can I cancel my subscription at any time?",
                a: "Yes. You can cancel anytime from your billing page. If you cancel mid-month you'll receive a prorated refund for unused days.",
              },
              {
                q: "What makes Property Manager different from Strata Council?",
                a: "The PM plan is built for companies managing multiple buildings. It adds a multi-building dashboard, daily digest emails across all properties, council-delegated permission controls (each council decides what you can modify), team member management, and cross-building analytics. It's the professional-grade tier for property management firms.",
              },
              {
                q: "What does 'council-delegated permissions' mean?",
                a: "Each strata council retains control over what their property manager can and cannot change — for example, whether the PM can modify strata fees or compliance rules. This is unique to Stratakey and ensures councils stay in governance while delegating day-to-day operations.",
              },
              {
                q: "Is HST or GST added to the price?",
                a: "Applicable Canadian taxes (GST/HST) will be calculated and shown during checkout based on your billing address.",
              },
              {
                q: "Do hosts need a subscription?",
                a: "No. Hosts always have a free account with full access to their bookings, documents, maintenance requests, guest portal, and the messaging feature.",
              },
              {
                q: "What payment methods are accepted?",
                a: "All major credit and debit cards via Stripe. We do not store your payment details — everything is handled securely by Stripe.",
              },
            ].map(({ q, a }) => (
              <div key={q} className="border-b pb-5">
                <dt className="text-sm font-semibold mb-1.5">{q}</dt>
                <dd className="text-sm text-muted-foreground leading-relaxed">{a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
